import { useEffect, useRef, useState } from "react";

import type {
  InPlaceRestoreConfirmationResponse,
  InPlaceRestoreHistory,
  InPlaceRestorePreparationResponse,
  InPlaceRestorePreview,
  InPlaceRestoreReport,
  InPlaceRestoreSummary,
} from "@board-preserve/contracts";

import {
  confirmCloudInPlaceRestore,
  controlCloudInPlaceRestore,
  prepareCloudInPlaceRestore,
  previewCloudInPlaceRestore,
  readActiveCloudInPlacePreparation,
  readCloudInPlacePreparation,
  readCloudInPlaceRestoreHistory,
  readCloudInPlaceRestoreReport,
} from "./cloud-connection";
import {
  canSubmitInPlaceConfirmation,
  inPlacePreparationPresentation,
  inPlaceRestoreChangeGroups,
  type InPlaceRestoreAcknowledgements,
} from "./in-place-restore-presentation";
import { progressTimeRemainingPresentation } from "./job-progress-presentation";
import type { TrelloAuthorizationClient } from "./trello-authorization";

type PreviewState = "failed" | "idle" | "loading" | "ready";
type ConfirmationState = "failed" | "idle" | "prepared" | "submitting";
type DiscoveryState = "failed" | "loading" | "ready";
type ReportState = "failed" | "idle" | "loading" | "ready";

export const SAFETY_BACKUP_TAB_NAVIGATION_MESSAGE =
  "Status refreshes automatically. You can switch to Backup and return here without restarting this safety backup.";

function emptyInPlaceAcknowledgements(): InPlaceRestoreAcknowledgements {
  return {
    changesOriginalBoard: false,
    comparisonReviewed: false,
    extrasRemainIntact: false,
    fidelityReviewed: false,
    noDeleteEndpoints: false,
    safetyBackupCompleted: false,
  };
}

function restoreStatusLabel(restore: InPlaceRestoreSummary): string {
  if (restore.controlState === "pause_requested") return "Pausing";
  if (restore.controlState === "cancel_requested") return "Cancelling";
  return {
    canceled: "Cancelled",
    complete: "Complete",
    complete_with_warnings: "Complete with warnings",
    failed: "Failed",
    paused: "Paused",
    pending: "Queued",
    running: "Restoring",
  }[restore.status];
}

function restoreProgress(restore: InPlaceRestoreSummary): number {
  if (restore.progress.total === 0) return 0;
  return Math.min(
    100,
    Math.round((restore.progress.completed / restore.progress.total) * 100),
  );
}

function readableResultCode(value: string): string {
  if (value === "operation_type_not_supported") {
    return "This change is not supported by original-board restore yet";
  }
  if (value === "postcondition_mismatch") {
    return "Trello created the item but normalized one or more captured values";
  }
  return value
    .replaceAll("_", " ")
    .replace(/^./u, (character) => character.toUpperCase());
}

function operationStatusLabel(operation: {
  action: string;
  destinationId: string | null;
  resultCode: string | null;
  status: string;
}): string {
  if (
    operation.resultCode === "postcondition_mismatch" &&
    operation.destinationId
  ) {
    return operation.action === "create"
      ? "created with warning"
      : "updated with warning";
  }
  return operation.status;
}

interface InPlaceRestorePanelProps {
  apiUrl: string | null;
  boardName: string;
  client: TrelloAuthorizationClient;
  executionEnabled: boolean;
  onSnapshotRequired?: (snapshotId: string) => void;
  snapshot: {
    capturedAt: string;
    id: string;
  } | null;
}

export function InPlaceRestorePanel({
  apiUrl,
  boardName,
  client,
  executionEnabled,
  onSnapshotRequired,
  snapshot,
}: InPlaceRestorePanelProps) {
  const [state, setState] = useState<PreviewState>("idle");
  const [preview, setPreview] = useState<InPlaceRestorePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [createFreshSafetyBackup, setCreateFreshSafetyBackup] = useState(true);
  const [discoveryState, setDiscoveryState] =
    useState<DiscoveryState>("loading");
  const [preparation, setPreparation] =
    useState<InPlaceRestorePreparationResponse | null>(null);
  const preparationKey = useRef<string | null>(null);
  const confirmationKey = useRef<string | null>(null);
  const onSnapshotRequiredRef = useRef(onSnapshotRequired);
  const [confirmationState, setConfirmationState] =
    useState<ConfirmationState>("idle");
  const [confirmation, setConfirmation] =
    useState<InPlaceRestoreConfirmationResponse | null>(null);
  const [restoreHistory, setRestoreHistory] =
    useState<InPlaceRestoreHistory | null>(null);
  const [restoreHistoryState, setRestoreHistoryState] =
    useState<ReportState>("idle");
  const [restoreHistoryError, setRestoreHistoryError] = useState<string | null>(
    null,
  );
  const [restoreReport, setRestoreReport] =
    useState<InPlaceRestoreReport | null>(null);
  const [restoreReportJobId, setRestoreReportJobId] = useState<string | null>(
    null,
  );
  const [restoreReportState, setRestoreReportState] =
    useState<ReportState>("idle");
  const [restoreReportError, setRestoreReportError] = useState<string | null>(
    null,
  );
  const [restoreControlJobId, setRestoreControlJobId] = useState<string | null>(
    null,
  );
  const [typedBoardName, setTypedBoardName] = useState("");
  const [confirmationAcknowledgements, setConfirmationAcknowledgements] =
    useState<InPlaceRestoreAcknowledgements>(emptyInPlaceAcknowledgements);
  const selectedSnapshotId = snapshot?.id ?? null;

  useEffect(() => {
    onSnapshotRequiredRef.current = onSnapshotRequired;
  }, [onSnapshotRequired]);

  useEffect(() => {
    setState("idle");
    setPreview(null);
    setError(null);
    setPreparing(false);
    setPreparation(null);
    preparationKey.current = null;
    confirmationKey.current = null;
    setConfirmationState("idle");
    setConfirmation(null);
    setTypedBoardName("");
    setConfirmationAcknowledgements(emptyInPlaceAcknowledgements());
    setDiscoveryState("loading");
  }, [selectedSnapshotId]);

  useEffect(() => {
    if (!selectedSnapshotId) {
      setDiscoveryState("ready");
      return;
    }
    let stopped = false;
    void (async () => {
      try {
        const session = await client.createCloudContextSession(
          "in-place-restore-status",
        );
        const result = await readActiveCloudInPlacePreparation(apiUrl, session);
        if (stopped) return;
        if (result.preparation) {
          if (result.preparation.targetSnapshotId !== selectedSnapshotId) {
            onSnapshotRequiredRef.current?.(
              result.preparation.targetSnapshotId,
            );
            return;
          }
          // A locally-started preparation is newer UI state than this GET. Do
          // not replace it if the discovery response arrives after the user
          // already started the guarded step.
          setPreparation((current) => current ?? result.preparation);
        }
        setDiscoveryState("ready");
      } catch (discoveryError) {
        if (stopped) return;
        setError(
          discoveryError instanceof Error
            ? discoveryError.message
            : "Existing safety backup status could not be checked.",
        );
        setDiscoveryState("failed");
      }
    })();
    return () => {
      stopped = true;
    };
  }, [apiUrl, client, selectedSnapshotId]);

  useEffect(() => {
    if (
      !preparation ||
      !["safety_backup_pending", "safety_backup_running"].includes(
        preparation.status,
      )
    ) {
      return;
    }
    let stopped = false;
    let refreshInFlight = false;
    const timer = window.setInterval(() => {
      if (refreshInFlight) {
        return;
      }
      refreshInFlight = true;
      void (async () => {
        try {
          const session = await client.createCloudContextSession(
            "in-place-restore-status",
          );
          const latest = await readCloudInPlacePreparation(
            apiUrl,
            session,
            preparation.preparationId,
          );
          if (!stopped) setPreparation(latest);
        } catch (pollError) {
          if (!stopped) {
            setError(
              pollError instanceof Error
                ? pollError.message
                : "The safety backup status could not be refreshed.",
            );
          }
        } finally {
          refreshInFlight = false;
        }
      })();
    }, 5_000);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [apiUrl, client, preparation]);

  async function loadRestoreHistory(silent = false): Promise<void> {
    if (!silent) setRestoreHistoryState("loading");
    setRestoreHistoryError(null);
    try {
      const session = await client.createCloudContextSession(
        "in-place-restore-status",
      );
      const result = await readCloudInPlaceRestoreHistory(apiUrl, session);
      setRestoreHistory(result);
      setRestoreHistoryState("ready");
      const active = result.restores.find((restore) =>
        ["pending", "running", "paused"].includes(restore.status),
      );
      const tracked =
        active ??
        result.restores.find((restore) => restore.jobId === restoreReportJobId);
      if (tracked) {
        await loadRestoreReport(tracked.jobId, true);
      }
    } catch (historyError) {
      setRestoreHistoryError(
        historyError instanceof Error
          ? historyError.message
          : "Original-board restore reports could not be loaded.",
      );
      setRestoreHistoryState("failed");
    }
  }

  async function loadRestoreReport(
    jobId: string,
    silent = false,
  ): Promise<void> {
    setRestoreReportJobId(jobId);
    if (!silent) setRestoreReportState("loading");
    setRestoreReportError(null);
    try {
      const session = await client.createCloudContextSession(
        "in-place-restore-status",
      );
      setRestoreReport(
        await readCloudInPlaceRestoreReport(apiUrl, session, jobId),
      );
      setRestoreReportState("ready");
    } catch (reportError) {
      setRestoreReportError(
        reportError instanceof Error
          ? reportError.message
          : "The original-board restore report could not be loaded.",
      );
      setRestoreReportState("failed");
    }
  }

  async function controlRestore(
    restore: InPlaceRestoreSummary,
    action: "cancel" | "pause" | "resume" | "retry",
  ): Promise<void> {
    setRestoreControlJobId(restore.jobId);
    setRestoreReportError(null);
    try {
      const session = await client.createCloudContextSession(
        "in-place-restore-control",
      );
      await controlCloudInPlaceRestore(apiUrl, session, restore.jobId, action);
      await loadRestoreHistory(true);
      await loadRestoreReport(restore.jobId, true);
    } catch (controlError) {
      setRestoreReportError(
        controlError instanceof Error
          ? controlError.message
          : "The original-board restore control could not be applied.",
      );
    } finally {
      setRestoreControlJobId(null);
    }
  }

  useEffect(() => {
    void loadRestoreHistory();
  }, [apiUrl, client]);

  const hasActiveRestore =
    restoreHistory?.restores.some((restore) =>
      ["pending", "running", "paused"].includes(restore.status),
    ) ?? false;

  useEffect(() => {
    if (!hasActiveRestore) return;
    const timer = window.setInterval(() => {
      void loadRestoreHistory(true);
    }, 2_000);
    return () => window.clearInterval(timer);
  }, [apiUrl, client, hasActiveRestore]);

  async function compareWithLiveBoard(): Promise<void> {
    if (!snapshot) {
      return;
    }
    setState("loading");
    setPreview(null);
    setError(null);
    const keepPreparation =
      preparation !== null &&
      preparation.targetSnapshotId === snapshot.id &&
      !["expired", "failed"].includes(preparation.status);
    if (!keepPreparation) {
      setPreparation(null);
      preparationKey.current = null;
    }
    confirmationKey.current = null;
    setConfirmationState("idle");
    setConfirmation(null);
    setTypedBoardName("");
    setConfirmationAcknowledgements(emptyInPlaceAcknowledgements());
    try {
      const session = await client.createCloudContextSession(
        "in-place-restore-preview",
      );
      const result = await previewCloudInPlaceRestore(
        apiUrl,
        session,
        snapshot.id,
      );
      setPreview(result);
      setState("ready");
    } catch (previewError) {
      setError(
        previewError instanceof Error
          ? previewError.message
          : "The backup could not be compared with the live board.",
      );
      setState("failed");
    }
  }

  async function prepareGuardedRestore(): Promise<void> {
    if (!snapshot || !preview || preview.comparison.totalChanges === 0) return;
    preparationKey.current ??= crypto.randomUUID();
    setPreparing(true);
    setError(null);
    try {
      const session = await client.createCloudContextSession(
        "in-place-restore-prepare",
      );
      setPreparation(
        await prepareCloudInPlaceRestore(apiUrl, session, {
          createFreshSafetyBackup,
          idempotencyKey: preparationKey.current,
          previewSha256: preview.previewSha256,
          targetSnapshotId: snapshot.id,
        }),
      );
    } catch (preparationError) {
      setError(
        preparationError instanceof Error
          ? preparationError.message
          : "The safety backup could not be prepared.",
      );
    } finally {
      setPreparing(false);
    }
  }

  async function confirmPreparedRestore(): Promise<void> {
    const preparationReady =
      preparation?.status === "ready_for_confirmation" &&
      preparation.planSha256 !== null &&
      preparation.safetyBackup.snapshotId !== null;
    const allowed = canSubmitInPlaceConfirmation({
      acknowledgements: confirmationAcknowledgements,
      boardName,
      executionEnabled,
      preparationReady,
      submitting: confirmationState === "submitting",
      typedBoardName,
    });
    if (!allowed || !snapshot || !preparation || !preparation.planSha256) {
      return;
    }

    confirmationKey.current ??= crypto.randomUUID();
    setConfirmationState("submitting");
    setError(null);
    try {
      const session = await client.createCloudContextSession(
        "in-place-restore-confirm",
      );
      const result = await confirmCloudInPlaceRestore(apiUrl, session, {
        acknowledgements: {
          changesOriginalBoard: true,
          comparisonReviewed: true,
          extrasRemainIntact: true,
          fidelityReviewed: true,
          noDeleteEndpoints: true,
          safetyBackupCompleted: true,
        },
        idempotencyKey: confirmationKey.current,
        planSha256: preparation.planSha256,
        preparationId: preparation.preparationId,
        targetSnapshotId: snapshot.id,
      });
      setConfirmation(result);
      setConfirmationState("prepared");
      setPreparation((current) =>
        current ? { ...current, status: "confirmed" } : current,
      );
      await loadRestoreHistory(true);
      await loadRestoreReport(result.jobId, true);
    } catch (confirmationError) {
      setError(
        confirmationError instanceof Error
          ? confirmationError.message
          : "The prepared original-board restore could not be confirmed.",
      );
      setConfirmationState("failed");
    }
  }

  const groups = preview ? inPlaceRestoreChangeGroups(preview) : [];
  const preparationPresentation = preparation
    ? inPlacePreparationPresentation(
        preparation.status,
        preparation.operationCount,
      )
    : null;
  const comparisonLocked =
    preparing ||
    (preview !== null &&
      (preparationPresentation?.active === true ||
        preparationPresentation?.readyForConfirmation === true));
  const preparationReady =
    preparation?.status === "ready_for_confirmation" &&
    preparation.planSha256 !== null &&
    preparation.safetyBackup.snapshotId !== null;
  const confirmationAllowed = canSubmitInPlaceConfirmation({
    acknowledgements: confirmationAcknowledgements,
    boardName,
    executionEnabled,
    preparationReady,
    submitting: confirmationState === "submitting",
    typedBoardName,
  });

  function updateConfirmationAcknowledgement(
    field: keyof InPlaceRestoreAcknowledgements,
    checked: boolean,
  ): void {
    setConfirmationAcknowledgements((current) => ({
      ...current,
      [field]: checked,
    }));
  }

  return (
    <section
      aria-labelledby="in-place-restore-title"
      className="in-place-restore-panel"
    >
      <header className="in-place-workflow-heading">
        <div>
          <strong id="in-place-restore-title">Guarded board recovery</strong>
          <span>Compare › protect › confirm</span>
        </div>
        <span className="advanced-chip">Advanced</span>
      </header>
      <div className="in-place-restore-body">
        <div className="in-place-warning">
          <strong>This changes the active Trello board</strong>
          <p>
            Board Preserve will only target <b>{boardName}</b> when its live
            board and Workspace IDs exactly match the selected backup. The safe
            new-board and one-item recovery options above remain the recommended
            routes.
          </p>
        </div>

        <div className="in-place-restore-stage">
          <div>
            <strong>1. Compare backup with live board</strong>
            <p>
              This dry run reads the current board and shows supported
              structural differences. It makes 0 Trello writes.
            </p>
            {snapshot ? (
              <small>
                Selected backup:{" "}
                {new Date(snapshot.capturedAt).toLocaleString()}
              </small>
            ) : (
              <small>
                Select a retained backup or validate an uploaded ZIP above to
                continue.
              </small>
            )}
          </div>
          <button
            disabled={!snapshot || state === "loading" || comparisonLocked}
            onClick={() => void compareWithLiveBoard()}
            type="button"
          >
            {state === "loading"
              ? "Comparing…"
              : preparation && !preview
                ? "Review comparison"
                : comparisonLocked
                  ? "Comparison locked"
                  : preparationPresentation?.restartRequired
                    ? "Compare again"
                    : "Compare with live board"}
          </button>
        </div>

        {error ? (
          <p className="in-place-restore-error" role="alert">
            {error}
          </p>
        ) : null}

        {preview ? (
          <div className="in-place-comparison" aria-live="polite">
            <div className="in-place-comparison-heading">
              <div>
                <strong>
                  {preview.comparison.totalChanges} structural difference
                  {preview.comparison.totalChanges === 1 ? "" : "s"}
                </strong>
                <span>
                  Exact board and Workspace identity confirmed · preview expires{" "}
                  {new Date(preview.expiresAt).toLocaleString()}
                </span>
              </div>
              <span className="dry-run-chip">Dry run · 0 writes</span>
            </div>

            {groups.length > 0 ? (
              <ul className="in-place-change-groups">
                {groups.map((group) => {
                  const counts = (
                    <span className="in-place-change-counts">
                      {group.restoreMissing} missing live · {group.changed}
                      {" changed · "}
                      {group.moved} moved · {group.liveOnly} live-only
                    </span>
                  );
                  return (
                    <li key={group.type}>
                      {group.details.length > 0 ? (
                        <details open={group.restoreMissing > 0}>
                          <summary>
                            <strong>{group.label}</strong>
                            {counts}
                          </summary>
                          <ul className="in-place-change-details">
                            {group.details.map((detail) => (
                              <li key={`${detail.outcome}:${detail.entityId}`}>
                                <div>
                                  <strong>{detail.label}</strong>
                                  <span
                                    className={`in-place-change-outcome in-place-change-outcome-${detail.outcome}`}
                                  >
                                    {detail.outcomeLabel}
                                  </span>
                                </div>
                                {detail.changedFields.length > 0 ? (
                                  <span>
                                    Changed: {detail.changedFields.join(", ")}
                                  </span>
                                ) : null}
                                <small title={detail.entityId}>
                                  Trello ID: {detail.entityId}
                                </small>
                              </li>
                            ))}
                          </ul>
                          {group.hiddenDetailCount > 0 ? (
                            <small className="in-place-change-overflow">
                              {group.hiddenDetailCount} more named change
                              {group.hiddenDetailCount === 1 ? "" : "s"} not
                              shown in this compact view.
                            </small>
                          ) : null}
                        </details>
                      ) : (
                        <div className="in-place-change-summary-only">
                          <strong>{group.label}</strong>
                          {counts}
                          <small>
                            Individual historical records remain preserved in
                            the backup but are not replayed as Trello audit
                            history.
                          </small>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="in-place-no-changes">
                No supported structural differences were detected. There is
                nothing to prepare from this backup.
              </p>
            )}

            <div className="in-place-policy">
              <strong>Planned safety policy</strong>
              <ul>
                <li>
                  A safety backup matching the live board must be bound to the
                  plan; creating a fresh one is recommended.
                </li>
                <li>Missing supported items may be recreated.</li>
                <li>
                  Supported values may be updated to their captured values.
                </li>
                <li>Extra live items stay in place.</li>
                <li>
                  Original actors, timestamps, IDs, and audit history cannot be
                  recreated.
                </li>
              </ul>
            </div>
          </div>
        ) : null}

        <div className="in-place-restore-stage in-place-restore-stage-locked">
          <div>
            <strong>2. Create safety backup and prepare changes</strong>
            <p>
              This guarded step will capture the live board first, then bind a
              resumable plan to this exact comparison. The held safety backup is
              created before any restore write.
            </p>
            {preparationPresentation ? (
              <div
                aria-live="polite"
                className={`in-place-preparation-status in-place-preparation-status-${preparationPresentation.tone}`}
                role="status"
              >
                <strong>{preparationPresentation.label}</strong>
                <span>{preparationPresentation.message}</span>
                {preparationPresentation.active ? (
                  <small>{SAFETY_BACKUP_TAB_NAVIGATION_MESSAGE}</small>
                ) : preparationPresentation.restartRequired ? (
                  <small>
                    Use Compare again above before retrying. A failed safety
                    backup cannot unlock or change the live board.
                  </small>
                ) : null}
              </div>
            ) : null}
            {!preparation ? (
              <label className="in-place-safety-choice">
                <input
                  checked={createFreshSafetyBackup}
                  disabled={preparing || discoveryState !== "ready"}
                  onChange={(event) => {
                    setCreateFreshSafetyBackup(event.currentTarget.checked);
                    preparationKey.current = null;
                  }}
                  type="checkbox"
                />
                <span>
                  <b>Create a fresh safety backup</b>
                  <small>
                    Recommended and enabled by default. Turn this off only to
                    reuse a retained safety backup that Board Preserve can prove
                    exactly matches the current board. Restore never proceeds
                    without a matching recovery point.
                  </small>
                </span>
              </label>
            ) : preparation?.safetyBackup.mode === "reused" ? (
              <p className="in-place-safety-reused" role="status">
                An existing safety backup exactly matched the reviewed live
                board and was reused. No duplicate safety capture was made.
              </p>
            ) : null}
          </div>
          <button
            disabled={
              preparing ||
              discoveryState !== "ready" ||
              preparation !== null ||
              !preview ||
              preview.comparison.totalChanges === 0
            }
            onClick={() => void prepareGuardedRestore()}
            type="button"
          >
            {preparing
              ? createFreshSafetyBackup
                ? "Requesting safety backup…"
                : "Verifying existing safety backup…"
              : discoveryState === "loading"
                ? "Checking safety status…"
                : discoveryState === "failed"
                  ? "Safety status unavailable"
                  : preparationPresentation?.active
                    ? "Safety backup in progress"
                    : preparationPresentation?.readyForConfirmation
                      ? "Safety backup complete"
                      : createFreshSafetyBackup
                        ? "Create safety backup"
                        : "Reuse matching safety backup"}
          </button>
        </div>

        <div className="in-place-restore-stage in-place-confirmation-stage">
          <div>
            <strong>3. Review and confirm original-board changes</strong>
            <p>
              Reaching this stage never applies changes automatically. The
              preparing administrator must review the immutable plan and
              explicitly confirm it before any future restore execution.
            </p>
            {!preparationReady && confirmationState !== "prepared" ? (
              <small aria-live="polite">
                Locked until a matching safety backup is bound and the prepared
                plan is verified.
              </small>
            ) : null}
          </div>

          {preparationReady || confirmationState === "prepared" ? (
            <div className="in-place-confirmation-review">
              <div
                className={
                  executionEnabled
                    ? "in-place-execution-notice"
                    : "in-place-execution-notice in-place-execution-notice-disabled"
                }
                role="status"
              >
                <strong>
                  {executionEnabled
                    ? "Final confirmation available"
                    : "Final confirmation disabled in this build"}
                </strong>
                <span>
                  {executionEnabled
                    ? "Submitting queues this immutable plan for guarded, resumable execution. Supported changes will be applied to the exact original board."
                    : "You can review the safeguards below, but this build cannot submit them. No confirmation request or Trello restore write will be made."}
                </span>
              </div>

              <div className="in-place-confirmation-checks">
                <label>
                  <input
                    checked={confirmationAcknowledgements.changesOriginalBoard}
                    disabled={confirmationState === "prepared"}
                    onChange={(event) =>
                      updateConfirmationAcknowledgement(
                        "changesOriginalBoard",
                        event.currentTarget.checked,
                      )
                    }
                    type="checkbox"
                  />
                  I understand this plan is for the exact original board,{" "}
                  <b>{boardName}</b>, and execution will change that active
                  board.
                </label>
                <label>
                  <input
                    checked={confirmationAcknowledgements.comparisonReviewed}
                    disabled={confirmationState === "prepared"}
                    onChange={(event) =>
                      updateConfirmationAcknowledgement(
                        "comparisonReviewed",
                        event.currentTarget.checked,
                      )
                    }
                    type="checkbox"
                  />
                  I reviewed the backup-to-live-board comparison above.
                </label>
                <label>
                  <input
                    checked={confirmationAcknowledgements.extrasRemainIntact}
                    disabled={confirmationState === "prepared"}
                    onChange={(event) =>
                      updateConfirmationAcknowledgement(
                        "extrasRemainIntact",
                        event.currentTarget.checked,
                      )
                    }
                    type="checkbox"
                  />
                  I understand extra items that exist only on the live board
                  remain intact.
                </label>
                <label>
                  <input
                    checked={confirmationAcknowledgements.fidelityReviewed}
                    disabled={confirmationState === "prepared"}
                    onChange={(event) =>
                      updateConfirmationAcknowledgement(
                        "fidelityReviewed",
                        event.currentTarget.checked,
                      )
                    }
                    type="checkbox"
                  />
                  I reviewed the limitations for actors, timestamps, IDs,
                  activity, comments, and covers.
                </label>
                <label>
                  <input
                    checked={confirmationAcknowledgements.noDeleteEndpoints}
                    disabled={confirmationState === "prepared"}
                    onChange={(event) =>
                      updateConfirmationAcknowledgement(
                        "noDeleteEndpoints",
                        event.currentTarget.checked,
                      )
                    }
                    type="checkbox"
                  />
                  I understand this plan does not use Trello delete endpoints.
                </label>
                <label>
                  <input
                    checked={confirmationAcknowledgements.safetyBackupCompleted}
                    disabled={confirmationState === "prepared"}
                    onChange={(event) =>
                      updateConfirmationAcknowledgement(
                        "safetyBackupCompleted",
                        event.currentTarget.checked,
                      )
                    }
                    type="checkbox"
                  />
                  I confirm the fresh safety backup completed before this
                  prepared plan was offered.
                </label>
              </div>

              <label className="in-place-board-name-confirmation">
                <span>
                  Type <b>{boardName}</b> exactly to confirm the destination
                </span>
                <input
                  aria-describedby="in-place-board-name-help"
                  autoComplete="off"
                  disabled={confirmationState === "prepared"}
                  onChange={(event) =>
                    setTypedBoardName(event.currentTarget.value)
                  }
                  spellCheck={false}
                  type="text"
                  value={typedBoardName}
                />
                <small id="in-place-board-name-help">
                  This comparison is bound to the board and Workspace IDs; the
                  typed name is an additional deliberate-action check.
                </small>
              </label>

              <button
                className="in-place-confirm-button"
                disabled={
                  !confirmationAllowed || confirmationState === "prepared"
                }
                onClick={() => void confirmPreparedRestore()}
                type="button"
              >
                {confirmationState === "submitting"
                  ? "Confirming prepared plan…"
                  : confirmationState === "prepared"
                    ? confirmation?.dispatchStatus === "queued"
                      ? "Restore queued"
                      : "Prepared — execution disabled"
                    : executionEnabled
                      ? "Confirm prepared restore"
                      : "Confirmation disabled"}
              </button>

              {confirmation ? (
                <p className="in-place-confirmation-result" role="status">
                  {confirmation.dispatchStatus === "queued"
                    ? `Queued ${confirmation.operationCount} immutable operation${confirmation.operationCount === 1 ? "" : "s"}. Follow progress in the advanced restore report; pause or cancel remains available at safe checkpoints.`
                    : `Prepared ${confirmation.operationCount} immutable operation${confirmation.operationCount === 1 ? "" : "s"}. Server execution remains disabled; no Trello restore writes were dispatched.`}
                </p>
              ) : null}
            </div>
          ) : (
            <button disabled type="button">
              Final confirmation locked
            </button>
          )}
        </div>

        {restoreHistoryState === "loading" && !restoreHistory ? (
          <p className="in-place-report-loading" role="status">
            Checking original-board restore reports…
          </p>
        ) : null}
        {restoreHistoryError ? (
          <p className="in-place-restore-error" role="alert">
            {restoreHistoryError}
          </p>
        ) : null}
        {(restoreHistory?.restores.length ?? 0) > 0 ? (
          <section
            aria-labelledby="in-place-report-title"
            className="in-place-report-section"
          >
            <div className="in-place-report-heading">
              <div>
                <strong id="in-place-report-title">
                  Original-board restore reports
                </strong>
                <span>
                  Progress refreshes automatically while a restore is active.
                  Reports remain visible for 30 days.
                </span>
              </div>
              <button
                disabled={restoreHistoryState === "loading"}
                onClick={() => void loadRestoreHistory()}
                type="button"
              >
                {restoreHistoryState === "loading" ? "Refreshing…" : "Refresh"}
              </button>
            </div>

            <div className="in-place-report-list">
              {restoreHistory?.restores.slice(0, 4).map((restore) => {
                const percentage = restoreProgress(restore);
                const active = ["pending", "running", "paused"].includes(
                  restore.status,
                );
                return (
                  <article key={restore.jobId}>
                    <div className="in-place-report-summary">
                      <div>
                        <strong>{restoreStatusLabel(restore)}</strong>
                        <span>
                          Started {new Date(restore.createdAt).toLocaleString()}
                          {" · job "}
                          {restore.jobId.slice(0, 8)}
                        </span>
                        <span>
                          {restore.progress.completed} of{" "}
                          {restore.progress.total} operations ·{" "}
                          {restore.outcomes.applied} applied ·{" "}
                          {restore.outcomes.unchanged} unchanged ·{" "}
                          {restore.outcomes.skipped} skipped ·{" "}
                          {restore.outcomes.failed} failed
                        </span>
                      </div>
                      <div className="in-place-report-actions">
                        <button
                          disabled={
                            restoreReportState === "loading" &&
                            restoreReportJobId === restore.jobId
                          }
                          onClick={() => void loadRestoreReport(restore.jobId)}
                          type="button"
                        >
                          {restoreReport?.jobId === restore.jobId
                            ? "Refresh details"
                            : "View details"}
                        </button>
                        {active ? (
                          <>
                            <button
                              disabled={restoreControlJobId === restore.jobId}
                              onClick={() =>
                                void controlRestore(
                                  restore,
                                  restore.status === "paused"
                                    ? "resume"
                                    : "pause",
                                )
                              }
                              type="button"
                            >
                              {restoreControlJobId === restore.jobId
                                ? "Updating…"
                                : restore.status === "paused"
                                  ? "Resume"
                                  : "Pause"}
                            </button>
                            <button
                              className="danger"
                              disabled={restoreControlJobId === restore.jobId}
                              onClick={() =>
                                void controlRestore(restore, "cancel")
                              }
                              type="button"
                            >
                              Cancel
                            </button>
                          </>
                        ) : restore.status === "failed" ? (
                          <button
                            disabled={restoreControlJobId === restore.jobId}
                            onClick={() =>
                              void controlRestore(restore, "retry")
                            }
                            type="button"
                          >
                            {restoreControlJobId === restore.jobId
                              ? "Preparing retry…"
                              : "Retry unfinished items"}
                          </button>
                        ) : null}
                      </div>
                    </div>
                    <div
                      className={`in-place-report-progress in-place-report-progress-${restore.status}`}
                    >
                      <div>
                        <span>Restore progress</span>
                        <strong>{percentage}%</strong>
                      </div>
                      <progress
                        aria-label={`${restoreStatusLabel(restore)}: ${percentage}%`}
                        max={Math.max(restore.progress.total, 1)}
                        value={restore.progress.completed}
                      />
                      <span className="job-progress-eta">
                        {progressTimeRemainingPresentation({
                          completed: restore.progress.completed,
                          controlState: restore.controlState,
                          startedAt: restore.startedAt,
                          status: restore.status,
                          total: restore.progress.total,
                        })}
                      </span>
                    </div>
                    {restoreReportError &&
                    restoreReportJobId === restore.jobId ? (
                      <p className="in-place-restore-error" role="alert">
                        {restoreReportError}
                      </p>
                    ) : null}
                    {restoreReport?.jobId === restore.jobId ? (
                      <ol className="in-place-operation-list">
                        {restoreReport.operations.map((operation) => (
                          <li key={operation.sequence}>
                            <span>
                              <b>#{operation.sequence}</b>{" "}
                              {operation.operationType.replaceAll("_", " ")}
                              {" · "}
                              {operationStatusLabel(operation)}
                            </span>
                            {operation.resultCode ? (
                              <small>
                                {readableResultCode(operation.resultCode)}
                              </small>
                            ) : null}
                          </li>
                        ))}
                      </ol>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </section>
        ) : null}
      </div>
    </section>
  );
}
